import { cardColors } from "@plasma/data/card-colors";
import { ArrowLeft, Layers, Monitor } from "lucide-react";

import { StationActionButtons } from "~/components/stations/station-action-buttons";
import { StationDeleteDialog } from "~/components/stations/station-delete-dialog";
import {
  getCardColorStyle,
  isFilterRoutingMode,
  isRoutingMode,
  type RoutingMode,
  type StationType,
} from "~/components/stations/station-edit-types";
import { KdsAdvancedSettingsSection } from "~/components/stations/station-kds-advanced";
import { KdsAudioSettingsSection } from "~/components/stations/station-kds-audio";
import { KdsCrossScreenSection } from "~/components/stations/station-kds-cross-screen";
import { KdsDisplaySettingsSection } from "~/components/stations/station-kds-display";
import { KdsTimingSection } from "~/components/stations/station-kds-timing";
import { LinkedItemsPreview } from "~/components/stations/station-linked-items";
import { PairedDevicesSection } from "~/components/stations/station-paired-devices";
import { StationPreviewCard } from "~/components/stations/station-preview-card";
import { PrinterSettingsSection } from "~/components/stations/station-printer-settings";
import { RoutingDialog } from "~/components/stations/station-routing-dialog";
import { useStationEdit } from "~/components/stations/use-station-edit";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Switch } from "~/components/ui/switch";
import { parseEnumValue } from "~/lib/parse-enum-value";

function EditRoutingButton({
  routingMode,
  categoriesCount,
  itemsCount,
  onClick,
}: {
  routingMode: RoutingMode;
  categoriesCount: number;
  itemsCount: number;
  onClick: () => void;
}) {
  const label = routingMode === "categories" ? "Categories" : "Items";
  const count = routingMode === "categories" ? categoriesCount : itemsCount;

  return (
    <Button onClick={onClick} variant="outline">
      <Layers className="mr-2 h-4 w-4" />
      Edit {label} ({count} selected)
    </Button>
  );
}

export function EditStationContent({
  station,
  handleClose,
}: {
  station: StationType;
  handleClose: () => void;
}) {
  const s = useStationEdit(station, handleClose);
  const colorValue = getCardColorStyle(s.color);

  return (
    <div className="flex flex-col gap-6 py-5">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button className="cursor-pointer" onClick={handleClose} type="button">
          <ArrowLeft className="size-6" />
        </button>
        <div>
          <h1 className="h4">Edit Station</h1>
          <p className="text-muted-foreground text-sm">
            Configure station settings and routing
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-6 lg:flex-row lg:gap-12">
        {/* Left column: Station info */}
        <div className="flex-1 space-y-6">
          <StationPreviewCard
            categoriesSelected={s.categoriesSelected}
            colorValue={colorValue}
            hasScreen={s.hasScreen}
            isDefault={station.isDefault ?? false}
            itemsSelected={s.itemsSelected}
            name={s.name}
            pairedDevicesCount={station.pairedDevices.length}
            routingMode={s.routingMode}
            selectedPrinterIds={s.selectedPrinterIds}
            statusBadge={s.statusBadge}
          />

          {/* Edit fields */}
          <div className="space-y-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="station-name">Station Name</Label>
              <Input
                id="station-name"
                onChange={(e) => s.setName(e.target.value)}
                placeholder="e.g., Grill, Bar, Expo"
                value={s.name}
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label>Color</Label>
              <Select onValueChange={s.setColor} value={s.color}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(cardColors).map(([key, val]) => (
                    <SelectItem key={key} value={key}>
                      <div className="flex items-center gap-2">
                        <div
                          className="h-4 w-4 rounded-full"
                          style={{
                            background: val.color,
                            border: `1px solid ${val.stroke}`,
                          }}
                        />
                        {val.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <button
              className="bg-accent flex w-full cursor-pointer items-center justify-between rounded-xl px-4 py-3 text-left"
              onClick={() => s.setHasScreen(!s.hasScreen)}
              type="button"
            >
              <div className="flex items-center gap-3">
                <Monitor className="h-5 w-5" />
                <div>
                  <p className="font-medium">Has KDS Screen</p>
                  <p className="text-muted-foreground text-sm">
                    Orders will appear on a display
                  </p>
                </div>
              </div>
              <Switch checked={s.hasScreen} />
            </button>

            {s.hasScreen && (
              <PairedDevicesSection
                allDevices={s.allDevices}
                pairDeviceMutation={s.pairDeviceMutation}
                station={station}
                unpairDeviceMutation={s.unpairDeviceMutation}
                unpairedDevices={s.unpairedDevices}
              />
            )}

            <div className="flex flex-col gap-2">
              <Label>Routing Mode</Label>
              <Select
                onValueChange={(v) => {
                  const mode = parseEnumValue(v, isRoutingMode, "all");
                  s.setRoutingMode(mode);
                  if (mode === "all") {
                    s.setCategoriesSelected([]);
                    s.setItemsSelected([]);
                  }
                }}
                value={s.routingMode}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    All Items (receives everything)
                  </SelectItem>
                  <SelectItem value="categories">
                    Filter by Categories
                  </SelectItem>
                  <SelectItem value="items">
                    Filter by Specific Items
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {s.routingMode !== "all" && (
              <EditRoutingButton
                categoriesCount={s.categoriesSelected.length}
                itemsCount={s.itemsSelected.length}
                onClick={() => s.setIsRoutingDialogOpen(true)}
                routingMode={s.routingMode}
              />
            )}

            <PrinterSettingsSection
              autoPrint={s.autoPrint}
              printersAvailable={s.printersAvailable}
              printOrderTypes={s.printOrderTypes}
              printType={s.printType}
              selectedPrinterIds={s.selectedPrinterIds}
              setAutoPrint={s.setAutoPrint}
              setPrintOrderTypes={s.setPrintOrderTypes}
              setPrintType={s.setPrintType}
              setSelectedPrinterIds={s.setSelectedPrinterIds}
            />

            {s.hasScreen && (
              <div className="space-y-3 pt-4">
                <p className="text-muted-foreground text-sm font-medium">
                  KDS Display Settings
                </p>

                <KdsDisplaySettingsSection
                  displayOptions={s.displayOptions}
                  isOpen={s.isDisplayOpen}
                  setDisplayOptions={s.setDisplayOptions}
                  setIsOpen={s.setIsDisplayOpen}
                />

                <KdsAudioSettingsSection
                  audioSettings={s.audioSettings}
                  isOpen={s.isAudioOpen}
                  setAudioSettings={s.setAudioSettings}
                  setIsOpen={s.setIsAudioOpen}
                />

                <KdsTimingSection
                  isOpen={s.isTimingOpen}
                  setIsOpen={s.setIsTimingOpen}
                  setTimingThresholds={s.setTimingThresholds}
                  timingThresholds={s.timingThresholds}
                />

                <KdsCrossScreenSection
                  crossScreenMode={s.crossScreenMode}
                  isOpen={s.isWorkflowOpen}
                  nextStationId={s.nextStationId}
                  otherStations={s.otherStations}
                  setCrossScreenMode={s.setCrossScreenMode}
                  setIsOpen={s.setIsWorkflowOpen}
                  setNextStationId={s.setNextStationId}
                  setStationRole={s.setStationRole}
                  stationRole={s.stationRole}
                />

                <KdsAdvancedSettingsSection
                  createTicketPerSend={s.createTicketPerSend}
                  isOpen={s.isAdvancedOpen}
                  modifierKeywords={s.modifierKeywords}
                  setCreateTicketPerSend={s.setCreateTicketPerSend}
                  setIsOpen={s.setIsAdvancedOpen}
                  setModifierKeywords={s.setModifierKeywords}
                  setSortMode={s.setSortMode}
                  sortMode={s.sortMode}
                />
              </div>
            )}
          </div>

          <StationActionButtons
            hasChanges={s.hasChanges}
            isDefault={station.isDefault ?? false}
            onCancel={handleClose}
            onDelete={() => s.setIsDeleteOpen(true)}
            onSave={s.handleSave}
            onSetDefault={() =>
              s.setDefaultStationMutation.mutate({ id: station._id })
            }
            savePending={s.updateStationMutation.isPending}
            setDefaultPending={s.setDefaultStationMutation.isPending}
          />
        </div>

        {s.routingMode !== "all" && (
          <LinkedItemsPreview
            categoriesSelected={s.categoriesSelected}
            itemsSelected={s.itemsSelected}
            routingMode={s.routingMode}
          />
        )}
      </div>

      <StationDeleteDialog
        colorValue={colorValue}
        deletePending={s.deleteStationMutation.isPending}
        isOpen={s.isDeleteOpen}
        onDelete={s.handleDelete}
        onOpenChange={s.setIsDeleteOpen}
        stationName={station.name}
      />

      {/* Routing selection dialog */}
      <RoutingDialog
        categoriesSelected={s.categoriesSelected}
        color={colorValue}
        isOpen={s.isRoutingDialogOpen}
        itemsSelected={s.itemsSelected}
        onClose={() => s.setIsRoutingDialogOpen(false)}
        routingMode={
          isFilterRoutingMode(s.routingMode) ? s.routingMode : "categories"
        }
        setCategoriesSelected={s.setCategoriesSelected}
        setItemsSelected={s.setItemsSelected}
        stationName={s.name}
      />
    </div>
  );
}
